/**
 * VmInlineCard — inline card for VM citations (vm_vendor / vm_pr /
 * vm_contract / vm_po). Sprint AI-MODULE v2.3 AI-61 + AI-64.
 *
 * Renders alongside the assistant's prose (via AssistantText) for VM
 * references. Surfaces state + days-in-state + prerequisites + blocker
 * attribution, plus action-aware CTA buttons derived from
 * vmCtaResolver. The CTAs are always navigations — the AI never
 * executes a vendor workflow transition itself.
 */
import { type FC } from 'react';
import {
  AlertTriangle,
  Briefcase,
  ClipboardList,
  ExternalLink,
  FileSignature,
  Package,
  ShieldAlert,
  Users,
} from 'lucide-react';
import type { CitationRef } from '../api/aiApi';
import { resolveVmCtas, type VmCta } from './vmCtaResolver';

interface Props {
  citation: CitationRef;
}

const VmInlineCard: FC<Props> = ({ citation }) => {
  const ctas = resolveVmCtas(citation);
  const kindLabel = vmKindLabel(citation.kind);
  const stateLabel = prettyState(citation.entity_state);
  const days = citation.days_in_state;
  const isUrgent = days != null && days >= (citation.kind === 'vm_pr' ? 7 : 10);
  const prereqs = citation.prerequisites_met;

  return (
    <div className={'ai-inline-card ai-inline-card--vm' + (isUrgent ? ' is-urgent' : '')}>
      <a className="ai-inline-card__header" href={citation.url}>
        <span className="ai-inline-card__icon">{vmKindIcon(citation.kind, 18)}</span>
        <span className="ai-inline-card__title-block">
          <span className="ai-inline-card__title">{citation.label}</span>
          <span className="ai-inline-card__sub">
            {kindLabel}
            {stateLabel && ` · ${stateLabel}`}
            {days != null && ` · ${days.toFixed(0)}d in state`}
            {isUrgent && (
              <span className="ai-inline-card__urgent">
                <AlertTriangle size={11} /> needs attention
              </span>
            )}
          </span>
        </span>
        <ExternalLink size={14} className="ai-inline-card__out" />
      </a>

      <div className="ai-inline-card__rows">
        {citation.owner_name && (
          <div className="ai-inline-card__row">
            <span className="ai-inline-card__row-label">Owner</span>
            <span>{citation.owner_name}</span>
          </div>
        )}
        {prereqs === true && (
          <div className="ai-inline-card__row ai-inline-card__row--ok">
            <span className="ai-inline-card__row-label">Prereqs</span>
            <span>Met — ready to approve</span>
          </div>
        )}
        {prereqs === false && (
          <div className="ai-inline-card__row ai-inline-card__row--warn">
            <span className="ai-inline-card__row-label">Prereqs</span>
            <span>
              Not met — needs Risk Q'naire marked complete OR ≥1 compliance doc
            </span>
          </div>
        )}
        {citation.current_blocker_role && citation.current_blocker_user_name && (
          <div className="ai-inline-card__row">
            <span className="ai-inline-card__row-label">
              {citation.current_blocker_role === 'requester' ? 'Waiting on' : 'Pending'}
            </span>
            <span>
              {citation.current_blocker_user_name} ({prettyRole(citation.current_blocker_role)})
            </span>
          </div>
        )}
        {citation.current_blocker_role && !citation.current_blocker_user_name && (
          <div className="ai-inline-card__row">
            <span className="ai-inline-card__row-label">Pending</span>
            <span>{prettyRole(citation.current_blocker_role)}</span>
          </div>
        )}
      </div>

      <div className="ai-inline-card__actions">
        {ctas.map((cta) => (
          <CtaButton key={cta.label} cta={cta} />
        ))}
      </div>
    </div>
  );
};

export default VmInlineCard;


// ── helpers ─────────────────────────────────────────────────────────

const CtaButton: FC<{ cta: VmCta }> = ({ cta }) => {
  const cls =
    cta.variant === 'primary'
      ? 'btn btn--sm btn--primary'
      : cta.variant === 'secondary'
      ? 'btn btn--sm btn--secondary'
      : 'btn btn--sm btn--ghost';
  if (cta.external) {
    return (
      <a className={cls} href={cta.href} target="_blank" rel="noopener noreferrer">
        {cta.label}
      </a>
    );
  }
  return (
    <a className={cls} href={cta.href}>
      {cta.label}
    </a>
  );
};

function vmKindIcon(kind: string, size: number) {
  switch (kind) {
    case 'vm_vendor':
      return <Briefcase size={size} />;
    case 'vm_pr':
      return <ClipboardList size={size} />;
    case 'vm_contract':
      return <FileSignature size={size} />;
    case 'vm_po':
      return <Package size={size} />;
    default:
      return <ShieldAlert size={size} />;
  }
}

function vmKindLabel(kind: string): string {
  switch (kind) {
    case 'vm_vendor':
      return 'Vendor';
    case 'vm_pr':
      return 'Procurement Request';
    case 'vm_contract':
      return 'Contract';
    case 'vm_po':
      return 'Purchase Order';
    default:
      return kind;
  }
}

function prettyState(state: string | undefined): string {
  if (!state) return '';
  const map: Record<string, string> = {
    DRAFT: 'Draft',
    START: 'Draft',
    PENDING_INTAKE: 'Pending intake',
    WAITING_FOR_USER_DATA: 'Waiting on requester',
    INFO_REQUEST_RETURN: 'Info request returned',
    SECURITY_REVIEW: 'InfoSec review',
    VENDOR_APPROVED: 'Approved',
    CANCELLED: 'Cancelled',
    REJECTED: 'Rejected',
    RECERTIFICATION_DUE: 'Recert due',
    pending_approval: 'Pending approval',
    approved: 'Approved',
    draft: 'Draft',
    rejected: 'Rejected',
    completed: 'Completed',
    closed: 'Closed',
  };
  return map[state] ?? state;
}

function prettyRole(role: string): string {
  const map: Record<string, string> = {
    infosec_approver: 'InfoSec',
    requester: 'requester',
    approver: 'approver',
  };
  if (role.startsWith('approver_tier_')) return `tier ${role.slice('approver_tier_'.length)} approvers`;
  return map[role] ?? role;
}

// Avoid unused-symbol warning when no consumers reach `Users` — keep
// the import so future enhancements can drop in.
void Users;
